import { Account } from './types';
import { isOrganizationView } from './accountUtils';

function normalizeSelectedAccountIds(selectedAccountIds?: string[]): string[] {
  if (!selectedAccountIds?.length) {
    return [];
  }

  return [...new Set(selectedAccountIds.map((id) => id.trim()).filter(Boolean))];
}

const isChildOfOrganization = (account: Account, organizationId: string): boolean =>
  account.parentAccountId === organizationId && account.accountId !== organizationId;

/**
 * Gets all child accounts for a given organization account.
 *
 * @param accounts - Array of all accounts available to the user
 * @param organizationId - The ID of the organization account
 * @returns Array of child accounts belonging to the organization
 */
export const getOrganizationChildAccounts = (
  accounts: Account[] | undefined,
  organizationId: string | undefined
): Account[] => {
  if (!accounts || !organizationId) {
    return [];
  }

  return accounts.filter((account) => isChildOfOrganization(account, organizationId));
};

/**
 * Hook to get organization status and child accounts for the current active account.
 *
 * @param accounts - Array of all accounts from AuthContext
 * @param activeTenantId - Active tenant ID from AuthContext
 * @returns Object with organization status and child accounts
 */
export const useOrganizationStatus = (
  accounts: Account[] | undefined,
  activeTenantId: string | undefined
) => {
  const childAccounts = getOrganizationChildAccounts(accounts, activeTenantId);

  return {
    isOrganizationAccount: childAccounts.length > 0,
    childAccounts,
  };
};

/**
 * Returns the Org View scope value for backend APIs.
 * In organization context, backend expects accounts=all for aggregated data.
 */
export const getOrgAccountsScopeValue = (
  accounts: Account[] | undefined,
  activeTenantId: string | undefined
): string | undefined =>
  isOrganizationView(activeTenantId, accounts) ? 'all' : undefined;

export const getAccountsScopeValue = (
  accounts: Account[] | undefined,
  activeTenantId: string | undefined,
  selectedAccountIds?: string[]
): string | undefined => {
  const normalizedSelectedIds = normalizeSelectedAccountIds(selectedAccountIds);
  if (normalizedSelectedIds.length > 0) {
    return normalizedSelectedIds.join(',');
  }

  return getOrgAccountsScopeValue(accounts, activeTenantId);
};

/**
 * Appends the org scope query parameter when user is in organization context.
 */
export const appendOrgAccountsScope = (
  params: URLSearchParams,
  accounts: Account[] | undefined,
  activeTenantId: string | undefined
): void => {
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId);
  if (accountsScope) {
    params.set('accounts', accountsScope);
  }
};

export const appendAccountsScope = (
  params: URLSearchParams,
  accounts: Account[] | undefined,
  activeTenantId: string | undefined,
  selectedAccountIds?: string[]
): void => {
  const accountsScope = getAccountsScopeValue(accounts, activeTenantId, selectedAccountIds);
  if (accountsScope) {
    params.set('accounts', accountsScope);
  }
};

/**
 * Stable segment for the loaded accounts graph (hydration). When `accounts`
 * arrives or changes, org detection can flip without `activeTenantId` changing.
 */
export const getAccountsGraphQueryKeySegment = (accounts: Account[] | undefined): string =>
  (accounts ?? [])
    .map((a) => a.accountId)
    .sort()
    .join(',');

/**
 * TanStack Query key parts for any request that uses org `accounts` query scope.
 * Keeps cache entries aligned with URL when tenant, org flag, or accounts list changes.
 */
export const getVolumeDataQueryScopeParts = (
  accounts: Account[] | undefined,
  activeTenantId: string | undefined,
  selectedAccountIds?: string[]
): readonly [string | null, string, string] => [
    activeTenantId ?? null,
    getAccountsScopeValue(accounts, activeTenantId, selectedAccountIds) ?? 'self',
    getAccountsGraphQueryKeySegment(accounts),
  ];
